/**
 * Shared column definitions used by every product-backed exception table.
 *
 * Changing a column here updates every product exception at once — that's
 * the whole point. Exception-specific overrides should live in the
 * per-exception config file, not here.
 *
 * Groups (rendered in a second header row above the column labels), in
 * table order - the table reads as a stock/outlet diagnosis first, with
 * pricing and activity as supporting context:
 *   - identity  → issues count, SKU, product name. Category, product type
 *                 (icon), and an Inactive chip are folded into the name
 *                 cell via renderProductNameWithMeta — no separate columns.
 *                 Frozen to the left edge on horizontal scroll.
 *   - outlets   → one (stock, avg cost) pair per outlet, built at render
 *                 time from `row.outlet_costs`, plus the trailing
 *                 "Total" stock column
 *   - pricing   → retail (ex tax), supplier (ex tax)
 *   - timing    → "Activity": last sold, last received
 *
 * Numeric / date columns in timing, pricing, and outlets all right-align
 * (heading and content) for consistent visual scanning.
 */
import React from 'react'
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { withTooltip } from './muiElements'
import { formatCurrency, formatDate } from '../../../utils/inspectorFormatters'
import { formatOutletName } from '../../../utils/outletUtils'
import {
  formatMainCategory,
  isMissingCategory,
  NO_CATEGORY_LABEL,
  NO_CATEGORY_VALUE,
} from '../../../utils/productCategory'
import {
  activePart,
  composeRowSubtitle,
  moneyPart,
  skuPart,
  textPart,
} from './rowSubtitle'
import { buildIssuesColumn, ISSUES_COLUMN_WIDTH } from './issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from './platformColumns'
import {
  buildInventoryFilterParams,
  sharedInventoryFilters,
} from './sharedInventoryFilters'
import {
  isActiveValue,
  renderCategoryName,
  renderProductNameWithMeta,
  resolveProductType,
  type OutletCostEntry,
} from './productRenderers'
import OutletMetricCell from './OutletMetricCell'
import { NUMERIC_FONT_SX } from '../../../theme/numericFont'
import type {
  ColumnGroupDef,
  ExceptionColumnDef,
  ExceptionConfig,
  ExceptionContext,
  ExceptionFilterDef,
  RenderContext,
} from '../types'

/**
 * Fixed widths for frozen identity columns. These feed both the CSS
 * width of the TableCell AND the cumulative-left offset math in
 * DataViewTable. Explicit fixed widths matter — minWidth alone
 * lets MUI pick actual widths that the sticky offsets can't track,
 * causing columns to slide under each other on horizontal scroll.
 */
const FROZEN_WIDTHS = {
  errors: ISSUES_COLUMN_WIDTH,
  name: 350,
} as const

export const PRODUCT_EXCEPTION_GROUPS: ColumnGroupDef[] = [
  { id: 'franchise', label: 'Franchise', separatorAfter: true },
  { id: 'identity', label: 'Product', separatorAfter: true },
  { id: 'outlets', label: 'Current Avg Cost / Stock by Outlet', separatorAfter: true },
  { id: 'pricing', label: 'Pricing', separatorAfter: true },
  { id: 'timing', label: 'Activity' },
]

/**
 * Column groups for a product exception config. The per-outlet group header
 * names the primary value first, matching the two cell lines the
 * `outletMode` drives: stock-focused reports read "Stock · Avg Cost",
 * price-focused reports read "Avg Cost · Stock". This header is the only
 * place the lines are labelled - the cells themselves carry no suffixes.
 * (Header cells render uppercase via `TableHeader`'s textTransform.)
 *
 * The header leads with "Current" because these per-outlet values are always
 * the product's live state (both the open view and the closed/resolved view,
 * which is enriched with current costs) - never a historical snapshot.
 */
export const buildProductExceptionGroups = (
  outletMode: 'cost' | 'stock' = 'cost',
): ColumnGroupDef[] =>
  PRODUCT_EXCEPTION_GROUPS.map(g =>
    g.id === 'outlets'
      ? {
          ...g,
          label:
            outletMode === 'stock'
              ? 'Current Stock / Avg Cost by Outlet'
              : 'Current Avg Cost / Stock by Outlet',
        }
      : g,
  )

/**
 * Platform-context columns tagged with the 'franchise' group so they sit
 * under a "Franchise" header on the platform variant. Inspector variant
 * hides them via `contexts: ['platform']` on each column.
 */
export const PRODUCT_FRANCHISE_COLUMNS: ExceptionColumnDef[] =
  PLATFORM_CONTEXT_COLUMNS.map(col => ({ ...col, group: 'franchise' }))

/**
 * Shared Main Category column for the simple-layout product exception
 * configs (variant-group dormant, xero-account error, supplier-variant
 * sold) that don't go through `buildProductExceptionConfig`. The richer
 * layout folds main category into the product name cell via
 * `renderProductNameWithMeta` instead - it doesn't need this column.
 */
export const MAIN_CATEGORY_COLUMN: ExceptionColumnDef = {
  key: 'category_root_name',
  label: 'Main Category',
  render: renderCategoryName,
}

/**
 * Inspector-side currency cell. The canonical numeric type treatment
 * (`NUMERIC_FONT_SX`) + 0.85rem matches the outlet cells so all numeric
 * columns scan as one block. Always normal weight - retail / supplier
 * prices are reference context, not the report's signal. $0 (or missing)
 * values render in the warning token so unset prices read as a quiet
 * flag rather than an alarm. Platform variant returns undefined to fall
 * through to the table's default currency formatter (keeps export/copy
 * behaviour unchanged).
 */
const renderInspectorCurrency = (
  value: any,
  _row: Record<string, any>,
  ctx: RenderContext,
): React.ReactNode => {
  if (ctx.variant !== 'inspector') return undefined
  const num = Number(value) || 0
  const isZero = num <= 0
  return React.createElement(
    Box,
    {
      component: 'span',
      sx: {
        ...NUMERIC_FONT_SX,
        fontSize: '0.85rem',
        fontWeight: 400,
        color: isZero ? 'warning.main' : 'text.primary',
      },
    },
    formatCurrency(num),
  )
}

const renderInspectorDate = (
  value: any,
  _row: Record<string, any>,
  ctx: RenderContext,
): React.ReactNode =>
  ctx.variant === 'inspector' && value ? formatDate(value) : value || '-'

/**
 * Identity columns — frozen to the left edge so they stay visible while
 * the rest of the row scrolls horizontally to reveal outlet columns.
 *
 * SKU, category, product type, and active status are folded into the
 * product name cell as a meta line (see `renderProductNameWithMeta`) —
 * keeping identity narrow while preserving the metadata.
 */
export const PRODUCT_EXCEPTION_IDENTITY_COLUMNS: ExceptionColumnDef[] = [
  // Issues column frozen-left so it stays alongside the auto-injected
  // bulk-select checkbox and the Product column - the identity band
  // remains visible while outlet columns scroll under it.
  { ...buildIssuesColumn(), freeze: 'left', group: 'identity' },
  {
    key: 'name',
    label: 'Product',
    align: 'left',
    width: FROZEN_WIDTHS.name,
    minWidth: FROZEN_WIDTHS.name,
    freeze: 'left',
    group: 'identity',
    render: renderProductNameWithMeta,
    link: { field: 'lightspeed_product_link', system: 'lightspeed' },
  },
]

export const PRODUCT_EXCEPTION_TIMING_COLUMNS: ExceptionColumnDef[] = [
  {
    key: 'last_sold',
    label: 'Last Sold',
    align: 'right',
    group: 'timing',
    render: renderInspectorDate,
    exportFormat: 'date',
  },
  {
    key: 'last_received',
    label: 'Last Received',
    align: 'right',
    group: 'timing',
    render: renderInspectorDate,
    exportFormat: 'date',
  },
]

export const PRODUCT_EXCEPTION_PRICING_COLUMNS: ExceptionColumnDef[] = [
  {
    key: 'price_excluding_tax',
    label: 'Retail\n(ex tax)',
    align: 'right',
    type: 'currency',
    minWidth: 92,
    group: 'pricing',
    render: renderInspectorCurrency,
    exportFormat: 'currency',
  },
  {
    key: 'supply_price',
    label: 'Supplier\n(ex tax)',
    align: 'right',
    type: 'currency',
    minWidth: 92,
    group: 'pricing',
    render: renderInspectorCurrency,
    exportFormat: 'currency',
  },
]

/**
 * Renderer for the platform variant's single "Outlets affected" cell.
 * Shows `M of N` as a small chip; hovering reveals the affected outlet
 * list with avg cost + qty so reviewers can spot the worst offenders
 * without leaving the row.
 *
 * Backend supplies the figures (`compute_affected_outlets` in
 * `backend/shared/exception_taggers/product_row_errors.py`) — frontend
 * just renders. Empty / missing means "no outlet data for this row".
 * Tooltip entries are looked up from ``outlet_costs`` using
 * ``affected_outlet_ids`` so we don't duplicate per-outlet payload in
 * the persisted job ``result_data``.
 */
const renderAffectedOutletsCell = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const total = Number(row.total_outlet_count ?? 0)
  const affected = Number(row.affected_outlet_count ?? 0)
  if (total === 0) {
    return React.createElement(
      Typography,
      { variant: 'caption', sx: { color: 'text.disabled' } },
      '-',
    )
  }
  const ids = new Set<string>((row.affected_outlet_ids || []) as string[])
  const all = (row.outlet_costs || []) as OutletCostEntry[]
  const list = all.filter((c) => ids.has(c.outlet_id))
  const tooltip = list.length === 0
    ? null
    : React.createElement(
        Stack,
        { sx: { py: 0.5 }, spacing: 0.5 },
        ...list.map((entry) => {
          const name = formatOutletName(entry.outlet_name, { shorten: true })
          const cost = formatCurrency(Number(entry.average_cost ?? 0))
          const stock = Number(
            entry.stock_quantity ?? entry.inventory_level ?? 0,
          )
          return React.createElement(
            Box,
            {
              key: entry.outlet_id,
              sx: {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ...NUMERIC_FONT_SX,
                fontSize: '0.75rem',
              },
            },
            React.createElement(
              Box,
              { component: 'span', sx: { flex: 1, fontFamily: 'inherit' } },
              name,
            ),
            React.createElement(
              Box,
              { component: 'span', sx: { fontFamily: 'inherit' } },
              cost,
            ),
            React.createElement(
              Box,
              { component: 'span', sx: { color: 'text.disabled' } },
              `Qty ${stock}`,
            ),
          )
        }),
      )
  const chip = React.createElement(Chip, {
    label: `${affected} of ${total}`,
    size: 'small',
    color: affected > 0 ? 'warning' : 'default',
    variant: affected > 0 ? 'filled' : 'outlined',
    sx: { height: 22, fontSize: '0.75rem', fontWeight: 600 },
  })
  if (!tooltip) return chip
  return withTooltip(
    { title: tooltip, arrow: true, placement: 'left' },
    React.createElement(Box, { component: 'span' }, chip),
  )
}

const buildPlatformOutletSummaryColumn = (): ExceptionColumnDef => ({
  key: 'affected_outlet_count',
  label: 'Outlets Affected',
  align: 'right',
  contexts: ['platform'],
  group: 'outlets',
  width: 140,
  minWidth: 120,
  render: renderAffectedOutletsCell,
  getSortValue: (row) => Number(row.affected_outlet_count ?? 0),
})

/**
 * Trailing "Total" column inside the outlet group: total stock across
 * all outlets in the same stock-chip style as the per-outlet cells
 * (stock-led, no cost line - there is no aggregate avg cost).
 * Keyed `total_stock` so existing default sorts and the export column
 * keep working. Replaces the old standalone "Total Stock" column.
 */
const buildAllOutletsColumn = (hasOutletColumns: boolean): ExceptionColumnDef => ({
  key: 'total_stock',
  label: 'Total',
  align: 'right',
  type: 'integer',
  width: 84,
  minWidth: 84,
  group: 'outlets',
  // Solid left rule (vs the dashed intra-band rules) marks where the
  // per-outlet band ends and the rollup begins - the totals-column
  // convention, without a background wash that would mute the stock
  // chip or add colour noise.
  getCellStyle: () =>
    hasOutletColumns ? { borderLeft: '1px solid #D9D9D9' } : {},
  render: (_value: any, row: Record<string, any>) => {
    const raw = row.total_stock
    const cell = React.createElement(OutletMetricCell, {
      stock: raw == null ? null : Number(raw),
      avgCost: null,
      primary: 'stock',
      showCost: false,
      variant: 'total',
    })
    return withTooltip(
      { title: 'Total stock across all outlets', arrow: true },
      React.createElement(Box, { component: 'span' }, cell),
    )
  },
  getSortValue: row => Number(row.total_stock) || 0,
})

/**
 * One (avg cost + stock qty) pair per outlet present in the dataset.
 * Built at render time so outlets that appear only on a subset of rows
 * still get a column (cells without an entry for that outlet render as
 * an em-dash). A "Total" stock column trails the band.
 *
 * Platform variant: skip the per-outlet column explosion (network-wide
 * results union every FP's outlets into hundreds of columns) and emit
 * a single summary column instead. The summary reads `M of N affected`
 * with hover detail; reviewers drill into the FP-side inspector for the
 * full per-outlet breakdown via the FP Code link.
 */
export type OutletColumnMode = 'cost' | 'stock'

export const buildOutletCostColumns = (
  rows: Record<string, any>[],
  variant: ExceptionContext,
  outletMode: OutletColumnMode = 'cost',
  expectsCost: boolean = true,
  outlets?: Array<{ id?: string; name?: string }>,
): ExceptionColumnDef[] => {
  if (variant === 'platform') {
    // Summary chip + the same Total column so the platform report
    // keeps the total-stock figure the old standalone column carried.
    return [buildPlatformOutletSummaryColumn(), buildAllOutletsColumn(true)]
  }
  const stockPrimary = outletMode === 'stock'
  // Prefer the FP's canonical outlet list (response.outlets) so EVERY outlet
  // gets a column regardless of which rows carry data - the closed view has no
  // anomalous rows, and the open view shouldn't drop an outlet just because no
  // row happens to flag it. Order is preserved from the API (backend sorts by
  // name), so ordering stays decided in one place. Falls back to the row
  // ``outlet_costs`` union (name-sorted) only when the canonical list is absent
  // - a safety net for callers that don't return ``outlets``.
  let entries: [string, string][]
  if (outlets && outlets.length > 0) {
    entries = outlets
      .filter((o): o is { id: string; name: string } => !!o.id && !!o.name)
      .map((o) => [o.id, o.name])
  } else {
    const outletMap = new Map<string, string>()
    for (const row of rows) {
      const costs = (row.outlet_costs || []) as Array<{
        outlet_id?: string
        outlet_name?: string
      }>
      for (const entry of costs) {
        if (entry.outlet_id && entry.outlet_name) {
          outletMap.set(entry.outlet_id, entry.outlet_name)
        }
      }
    }
    entries = Array.from(outletMap.entries()).sort(([, a], [, b]) =>
      a.localeCompare(b),
    )
  }
  const outletColumns = entries.map(([outletId, outletName], idx) => {
    const shortName = formatOutletName(outletName, { shorten: true })
    return {
      key: `outlet_${outletId}_cost`,
      label: shortName,
      align: 'right' as const,
      width: 78,
      minWidth: 78,
      group: 'outlets',
      // Subtle vertical rule between outlets (but not before the first
      // one — the group separator already handles that boundary).
      getCellStyle: () =>
        idx === 0
          ? {}
          : { borderLeft: '1px dashed #EDEDED' },
      render: (_value: any, row: Record<string, any>) => {
        const list = (row.outlet_costs || []) as Array<{
          outlet_id?: string
          average_cost?: number | null
          stock_quantity?: number | null
          inventory_level?: number | null
        }>
        const entry = list.find(c => c.outlet_id === outletId)
        // `null` = no record at this outlet (renders a muted dash) -
        // distinct from a genuine 0, which OutletMetricCell emphasises.
        const stockRaw = entry?.stock_quantity ?? entry?.inventory_level
        return React.createElement(OutletMetricCell, {
          stock: entry == null || stockRaw == null ? null : Number(stockRaw),
          avgCost:
            entry == null || entry.average_cost == null
              ? null
              : Number(entry.average_cost),
          primary: stockPrimary ? 'stock' : 'cost',
          expectsCost,
        })
      },
      getSortValue: (row: Record<string, any>) => {
        const list = (row.outlet_costs || []) as Array<{
          outlet_id?: string
          average_cost?: number
          stock_quantity?: number
          inventory_level?: number
        }>
        const entry = list.find(c => c.outlet_id === outletId)
        if (stockPrimary) {
          return Number(entry?.stock_quantity ?? entry?.inventory_level ?? 0)
        }
        return Number(entry?.average_cost ?? 0)
      },
    }
  })
  return [...outletColumns, buildAllOutletsColumn(outletColumns.length > 0)]
}

/**
 * Default filter set for product-pricing / product-setup exception views.
 * Category options are derived from the row data; product type uses fixed
 * options so it's still selectable on a result set that happens to lack a
 * given type. Active/inactive uses a single-choice select (default 'all'),
 * and the zero-stock toggle is off by default.
 */
export const buildProductExceptionFilters = (): ExceptionFilterDef[] => [
  {
    key: 'category_root_name',
    type: 'multiselect',
    label: 'Main Category',
    extractOptions: (rows) => {
      const counts = new Map<string, number>()
      for (const row of rows) {
        const raw = row.category_root_name
        const key = isMissingCategory(raw) ? NO_CATEGORY_VALUE : String(raw)
        counts.set(key, (counts.get(key) ?? 0) + 1)
      }
      return Array.from(counts.entries())
        .sort(([a], [b]) => {
          // "No category" sorts last so real categories scan first.
          if (a === NO_CATEGORY_VALUE) return 1
          if (b === NO_CATEGORY_VALUE) return -1
          return a.localeCompare(b)
        })
        .map(([value, count]) => ({
          value,
          label:
            value === NO_CATEGORY_VALUE
              ? NO_CATEGORY_LABEL
              : formatMainCategory(value),
          count,
          variant: value === NO_CATEGORY_VALUE ? ('subtle' as const) : undefined,
        }))
    },
    getValue: (row) => {
      const raw = row.category_root_name
      return isMissingCategory(raw) ? NO_CATEGORY_VALUE : String(raw)
    },
  },
  {
    key: 'product_type',
    type: 'multiselect',
    label: 'Type',
    options: [
      { value: 'simple', label: 'Standard' },
      { value: 'composite', label: 'Composites' },
      { value: 'variant', label: 'Variants' },
    ],
    // The classifier emits five buckets (variant_parent/variant_child,
    // composite_kit/composite_dosage, simple). Collapse them into the
    // three families the filter UI exposes so a row's family value matches
    // the selected option string.
    getValue: (row) => {
      const t = resolveProductType(row.product_type, row)
      if (!t) return undefined
      if (t === 'variant_parent' || t === 'variant_child') return 'variant'
      if (t === 'composite_kit' || t === 'composite_dosage') return 'composite'
      return 'simple'
    },
  },
  {
    key: 'active',
    type: 'select',
    label: 'Status',
    defaultValue: 'all',
    options: [
      { value: 'all', label: 'All statuses' },
      { value: 'active', label: 'Active only' },
      { value: 'inactive', label: 'Inactive only' },
    ],
    predicate: (row, value) => {
      const active = isActiveValue(row.active)
      return value === 'active' ? active : !active
    },
  },
  {
    key: 'has_stock',
    type: 'toggle',
    label: 'Hide zero-stock',
    defaultEnabled: false,
    predicate: (row) => Number(row.total_stock ?? 0) > 0,
  },
  {
    key: 'search',
    type: 'search',
    label: 'Search',
    placeholder: 'Search product name or SKU',
    // Visible TextField is hosted by the toolbar above the table. The
    // predicate runs locally against the loaded row set - matches
    // product name and SKU case-insensitively.
    predicate: (row, term) => {
      const needle = term.toLowerCase()
      const name = String(row.name ?? '').toLowerCase()
      if (name.includes(needle)) return true
      const sku = String(row.sku ?? '').toLowerCase()
      if (sku.includes(needle)) return true
      return false
    },
  },
]

export interface ProductExceptionConfigOptions {
  codes: string[]
  primaryErrorCode?: string
  defaultSortColumn?: string
  defaultSortDirection?: 'asc' | 'desc'
  rowKeyField?: string
  /** Per-config column overrides merged by key with the shared defaults. */
  columnOverrides?: ExceptionColumnDef[]
  /** Extra columns appended before the outlets group (e.g. exception-specific fields). */
  extraColumns?: ExceptionColumnDef[]
  /**
   * Pass through to `ExceptionConfig.containInCard`. Defaults to true
   * inside this builder because every product-exception table is wide
   * by construction - the `dynamicColumns: buildOutletCostColumns`
   * appended below adds one column per outlet (often 30+ on AU FPs).
   * Configs that don't want the contained-card model can pass false.
   */
  containInCard?: boolean
  /**
   * Controls the per-outlet column emphasis + group header. `'stock'`
   * leads each outlet cell with stock count under a "Stock · Avg Cost
   * (By Outlet)" group header; `'cost'` (default) leads with avg cost.
   * Use `'stock'` for stock-driven exceptions (negative stock, inactive
   * with stock).
   */
  outletMode?: OutletColumnMode
  /**
   * Whether a $0.00 avg cost is an anomaly on this report (default true:
   * ⚠ + warning colour in the outlet cells). Reports where zero cost is
   * the expected state (non-inventory products) pass false.
   */
  outletExpectsCost?: boolean
}

/**
 * Build a complete ExceptionConfig using the shared product-exception
 * column layout. Each per-exception config file composes its codes +
 * primary-error-code + any overrides through this helper so the column
 * layout stays consistent.
 */
export const buildProductExceptionConfig = (
  opts: ProductExceptionConfigOptions,
): ExceptionConfig => {
  const overrideMap = new Map<string, ExceptionColumnDef>()
  for (const col of opts.columnOverrides || []) overrideMap.set(col.key, col)
  const merge = (cols: ExceptionColumnDef[]) =>
    cols.map(c => ({ ...c, ...(overrideMap.get(c.key) || {}) }))

  return {
    codes: opts.codes,
    columns: [
      ...merge(PRODUCT_FRANCHISE_COLUMNS),
      ...merge(PRODUCT_EXCEPTION_IDENTITY_COLUMNS),
      ...(opts.extraColumns || []),
    ],
    columnGroups: buildProductExceptionGroups(opts.outletMode),
    // Pricing + activity trail the outlet band (the table reads as a
    // stock/outlet diagnosis first). Dynamic columns append after the
    // declared ones, so the supporting-context groups ride along here.
    dynamicColumns: (rows, variant, response) => [
      ...buildOutletCostColumns(
        rows,
        variant,
        opts.outletMode,
        opts.outletExpectsCost ?? true,
        response?.outlets as Array<{ id?: string; name?: string }> | undefined,
      ),
      ...merge(PRODUCT_EXCEPTION_PRICING_COLUMNS),
      ...merge(PRODUCT_EXCEPTION_TIMING_COLUMNS),
    ],
    // Shared inventory filters resolve server-side via
    // ``buildInventoryFilterParams`` so the Open/Closed pill counts
    // stay coherent with the table contents (see
    // ``docs/plans/pending/exceptions-filter-aware-envelope-counts.md``).
    filters: sharedInventoryFilters,
    buildFetchParams: (urlState) => ({
      ...buildInventoryFilterParams(urlState.filterValues),
    }),
    primaryErrorCode: opts.primaryErrorCode,
    defaultSortColumn: opts.defaultSortColumn ?? 'name',
    defaultSortDirection: opts.defaultSortDirection ?? 'asc',
    complexity: 'simple',
    rowKeyField: opts.rowKeyField ?? 'ls_id',
    containInCard: opts.containInCard ?? true,
    // Default product-row subtitle: SKU · Active/Inactive · Retail price ·
    // Main category. Surfaces the columns reviewers most often want to
    // see before drilling into the drawer. Per-config callers can
    // override by spreading and replacing.
    getRowSubtitle: (row) =>
      composeRowSubtitle([
        skuPart(row.sku),
        activePart(row.active ?? row.is_active),
        moneyPart(row.price_excluding_tax ?? row.price_excl_tax),
        textPart(formatMainCategory(row.category_root_name)),
      ]),
  }
}
